import React from 'react';
import {Box, styled} from '@mui/material';
import {SocialPlatform} from '@/types/platforms';
import LEGACY_COLORS from '@/styles/legacyColors';

export interface SocialNetworkButtonProps {
  platform: SocialPlatform;
  isLoading?: boolean;
  onClick: () => void;
  disabled?: boolean;
  hasError?: boolean;
}

const shouldNotForwardProps = ['platform', 'disabled', 'isLoading', 'hasError'];

const StyledButton = styled(Box, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})<{
  platform: string;
  disabled: boolean;
  isLoading: boolean;
  hasError: boolean;
}>(({platform, disabled, isLoading, hasError}) => ({
  'padding': '18px 0 18px 50px',
  'borderBottom': `solid 1px ${LEGACY_COLORS.GRAY_LIGHTER}`,
  'cursor': disabled || isLoading ? 'default' : 'pointer',
  'display': 'flex',
  'alignItems': 'center',
  'justifyContent': 'space-between',
  'color': hasError ? LEGACY_COLORS.RED : LEGACY_COLORS.GRAY,
  'fontSize': '15px',
  'lineHeight': '22px',
  'textTransform': 'capitalize',
  'opacity': disabled ? 0.6 : 1,
  'backgroundColor': 'transparent',

  // Platform-specific background icons
  'backgroundSize': '20px',
  'backgroundPosition': '15px center',
  'backgroundRepeat': 'no-repeat',

  '&::after': {
    content: '""',
    width: '12px',
    height: '12px',
    backgroundImage: "url('/assets/svg/icon-plus-sign.svg')",
    backgroundRepeat: 'no-repeat',
  },

  // Platform-specific background images
  ...(platform.toLowerCase() === 'instagram' && {
    backgroundImage: hasError
      ? "url('/assets/svg/logo-instagram-red.svg')"
      : "url('/assets/svg/logo-instagram-almost-black.svg')",
  }),
  ...(platform.toLowerCase() === 'tiktok' && {
    backgroundImage: hasError
      ? "url('/assets/svg/logo-tiktok-red.svg')"
      : "url('/assets/svg/logo-tiktok-almost-black.svg')",
  }),
  ...(platform.toLowerCase() === 'pinterest' && {
    backgroundImage: hasError
      ? "url('/assets/svg/logo-pinterest-red.svg')"
      : "url('/assets/svg/logo-pinterest-almost-black.svg')",
  }),
  // YouTube's Developer Policies (III.F.2a) forbid modifying the colors of the YouTube
  // icon: #212121 and the #FF0033 error red are the values YouTube permits, so this entry
  // is fixed by policy rather than by design. The other networks above use the same
  // almost-black purely so YouTube doesn't stand out.
  ...(platform.toLowerCase() === 'youtube' && {
    backgroundImage: hasError
      ? "url('/assets/svg/logo-youtube-red.svg')"
      : "url('/assets/svg/logo-youtube-almost-black.svg')",
  }),
  ...(platform.toLowerCase() === 'facebook' && {
    backgroundImage: hasError
      ? "url('/assets/svg/logo-facebook-red.svg')"
      : "url('/assets/svg/logo-facebook-almost-black.svg')",
  }),
}));

function SocialNetworkButton({
  platform,
  isLoading = false,
  onClick,
  disabled = false,
  hasError = false,
}: SocialNetworkButtonProps) {
  const handleClick = () => {
    if (!disabled && !isLoading) {
      onClick();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <StyledButton
      platform={platform}
      disabled={disabled}
      isLoading={isLoading}
      hasError={hasError}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
    >
      {platform}
    </StyledButton>
  );
}

export default SocialNetworkButton;
